import React, { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { FiMessageSquare, FiX, FiSend } from "react-icons/fi";
import LMW from "../assets/img/logo/buddha.png";

const SOCKET_URL = process.env.REACT_APP_URL;

export default function ChatBot() {
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [messages, setMessages] = useState<{ text: string; sender: string }[]>([
    { text: "How can we help you today?", sender: "Support" },
  ]);
  const [input, setInput] = useState("");
  const [socketId, setSocketId] = useState("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const socket = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleChat = () => {
    if (!isOpen) {
      setHasUnread(false);
    }
    setIsOpen(!isOpen);
  };

  const dropSupportMessage = () => {
    const contactMsg = {
      text: "It's taking us a bit longer than usual to respond. Please contact us at info@lastminutewellness.ca for immediate assistance.",
      sender: "system",
    };
    setMessages((prev) => [...prev, contactMsg]);
    setHasUnread(true);
  };

  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !socket.current) return;
    const msgData = { socketId, text: input };
    socket.current.emit("chat_message", msgData);
    setMessages((prev) => [...prev, { text: input, sender: "Me" }]);
    setInput("");

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(dropSupportMessage, 5 * 60 * 1000);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    socket.current = io(SOCKET_URL, {
      path: "/api/socket.io",
      transports: ["websocket"],
      withCredentials: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 5000,
    });
    socket.current.on("connect", () => {
      setSocketId(socket.current?.id || "");
    });
    socket.current.on("support_message", (data: { text: string }) => {
      setMessages((prev) => [...prev, { text: data.text, sender: "Support" }]);

      if (!isOpen) {
        setHasUnread(true);
      }
    });
    return () => {
      socket.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const checkVisibility = () => {
      const estHour = parseInt(
        new Intl.DateTimeFormat("en-US", {
          timeZone: "America/New_York",
          hour: "numeric",
          hour12: false,
        }).format(new Date()),
      );

      // Logic: Hide if 8 AM <= Hour < Midnight
      const shouldNotHide = estHour >= 8 && estHour < 24;
      if (shouldNotHide) {
        setTimeout(() => {
          setIsOpen(true);
        }, 1000 * 5);
      }
      setIsVisible(shouldNotHide);
    };

    checkVisibility();
    // Optional: Check every minute in case the user stays on the page
    const interval = setInterval(checkVisibility, 600000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {isVisible && (
        <button
          onClick={toggleChat}
          className="fixed bottom-[30px] right-[30px] z-[1001] w-12 h-12 rounded-2xl bg-blue-600 text-white border-none cursor-pointer flex items-center justify-center shadow-lg hover:scale-105 transition-all duration-200 ease-in-out"
        >
          {hasUnread && !isOpen && (
            <div className="absolute bottom-14 right-0 animate-bounce">
              <div className="bg-blue-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-xl shadow-lg whitespace-nowrap flex items-center gap-1">
                NEW MESSAGE
                <div className="absolute -bottom-1 right-5 w-2 h-2 bg-blue-600 rotate-45 border-r-1" />
              </div>
            </div>
          )}

          {isOpen ? <FiX size={24} /> : <FiMessageSquare size={24} />}
        </button>
      )}

      {isOpen && (
        <>
          {/* Overlay - Hidden on mobile as the chat is full screen */}
          <div
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 w-screen h-screen bg-black/20 z-[999] cursor-default hidden sm:block"
          />

          <div
            className="fixed 
            /* Mobile Styles: Full screen, no bottom offset */
            bottom-0 right-0 w-full h-full rounded-none 
            /* Desktop Styles: Windowed, offset from corner */
            sm:bottom-[100px] sm:right-[30px] sm:w-[360px] sm:h-[550px] sm:max-h-[80vh] sm:rounded-[24px] 
            flex flex-col z-[1000] shadow-2xl overflow-hidden border border-gray-100 bg-white animate-in slide-in-from-bottom-5 duration-300"
          >
            <div className="p-4 sm:p-5 flex items-center justify-between border-b border-gray-50 bg-white">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img
                    src={LMW}
                    alt="LMW-logo"
                    className="h-[35px] w-[35px] sm:h-[40px] sm:w-[40px] object-contain"
                  />
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                </div>
                <div>
                  <div className="font-semibold text-[14px] sm:text-[15px] text-gray-900 leading-tight">
                    Last Minute Wellness
                  </div>
                  <div className="text-[11px] text-gray-500">Support Team</div>
                </div>
              </div>
              <div
                className="p-2 -mr-2 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <FiX size={24} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-5 bg-white space-y-3">
              {messages.map((msg, i) => {
                const isMe = msg.sender === "Me";
                return (
                  <div
                    key={i}
                    className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                  >
                    {msg.sender === "system" ? (
                      <div
                        className="px-4 py-2.5 text-[15px] sm:text-base leading-relaxed max-w-[85%]
                            bg-gray-100 text-gray-800 rounded-2xl rounded-bl-none"
                      >
                        <p className="text-center text-xs text-gray-500 leading-relaxed">
                          It's taking us a bit longer than usual to respond.
                          Please contact us at{" "}
                          <a
                            href="mailto:info@lastminutewellness.ca"
                            className="text-blue-600 underline font-medium hover:text-blue-800"
                          >
                            info@lastminutewellness.ca
                          </a>{" "}
                          for immediate assistance.
                        </p>
                      </div>
                    ) : (
                      <div
                        className={`px-4 py-2.5 text-[15px] sm:text-base leading-relaxed max-w-[85%] ${
                          isMe
                            ? "bg-blue-600 text-white rounded-2xl rounded-br-none"
                            : "bg-gray-100 text-gray-800 rounded-2xl rounded-bl-none"
                        }`}
                      >
                        {msg.text}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form
              onSubmit={sendMessage}
              className="p-4 sm:p-5 pt-3 bg-white pb-[safe-area-inset-bottom]"
            >
              <div className="flex items-center bg-gray-100 rounded-xl px-3 py-2.5 sm:py-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 border-none outline-none text-[15px] sm:text-sm bg-transparent text-gray-800 placeholder-gray-400"
                />
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className={`bg-none border-none cursor-pointer flex items-center justify-center p-1 transition-colors ${
                    input.trim() ? "text-blue-600" : "text-gray-300"
                  }`}
                >
                  <FiSend size={20} />
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
}
